import axios from "axios";

import history from "@/app/history";
import { SESSION_EXPIRED_MESSAGE } from "@/constants/errorMessages";
import { parseApiError } from "@/utils/parseApiError";
import { getTabId } from "@/utils/tabId";
import {
  enqueueRequest,
  getIsRefreshing,
  handleLogout,
  isDefinitiveAuthFailure,
  processQueue,
  refreshAuthToken,
  setIsRefreshing,
} from "@/utils/tokenRefreshManager";

import { API_URL } from "../env";

const CancelToken = axios.CancelToken;
let cancel;

const onTrialEndedError = ({ status, errorCode }) => {
  if (status === 403 && errorCode === "trial_ended") {
    history.push("/trial-ended");
  }
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    onTrialEndedError({
      status: error?.response?.status,
      errorCode: error?.response?.data?.code,
    });

    // Skip refresh logic if this is the refresh token request itself
    if (originalRequest?.skipAuthRefresh) {
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized errors - try to refresh token first
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      if (getIsRefreshing()) {
        // If already refreshing, queue this request
        return enqueueRequest()
          .then((token) => {
            // Ensure headers object exists before assigning Authorization
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `${token.token_type} ${token.access_token}`;
            return axios(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      setIsRefreshing(true);

      try {
        console.log("%c Attempting to refresh token...", "color: blue");
        const newToken = await refreshAuthToken();
        console.log("%c Token refreshed successfully", "color: green");
        // Ensure headers object exists before assigning Authorization
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `${newToken.token_type} ${newToken.access_token}`;
        processQueue(null, newToken);
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);

        if (isDefinitiveAuthFailure(refreshError)) {
          console.log("%c Token refresh failed, logging out...", "color: orange");

          error.response.data = { detail: SESSION_EXPIRED_MESSAGE, code: "session_expired" };
          error.message = SESSION_EXPIRED_MESSAGE;
          error.parsed = parseApiError(error);

          setTimeout(() => {
            handleLogout();
          }, 100);
        }

        return Promise.reject(error);
      } finally {
        setIsRefreshing(false);
      }
    }

    error.parsed = parseApiError(error);
    return Promise.reject(error);
  }
);

export const createApi =
  (
    httpMethod,
    url,
    {
      headers = null,
      responseType = null,
      isUrlData = false,
      inputData = (payload) => payload,
      outputData = (response) => response,
    } = {}
  ) =>
  (auth, payload) => {
    const axiosOptions = {
      cancelToken: new CancelToken((c) => {
        cancel = c;
      }),
      method: httpMethod,
      url: API_URL + url,
      responseType: responseType || "json",
      headers: headers || {
        Accept: "application/json",
      },
      transformResponse: [outputData],
    };

    if (auth) {
      axiosOptions.headers.Authorization = `${auth.token_type} ${auth.access_token}`;
    }

    // Tag the client platform + per-tab id for the backend live-metrics middleware.
    axiosOptions.headers["X-Client-Platform"] = "web";
    axiosOptions.headers["X-Tab-Id"] = getTabId();

    if (httpMethod === "GET" || isUrlData) {
      axiosOptions.params = inputData(payload);
    } else {
      axiosOptions.data = inputData(payload);
    }

    return axios(axiosOptions);
  };

export const cancelOnGoinApiRequest = () => {
  cancel("Operation canceled by the user.");
};

export const mockApi =
  (fakeData, timeout = 2000) =>
  () =>
    new Promise((resolve) => {
      setTimeout(() => {
        resolve(fakeData);
      }, timeout);
    });
